import React, { useRef, useState } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import AddIcon from '@mui/icons-material/Add'
import { fileUpload, fileDelete } from '../../api/uploadApi'
import ValidationErrorText from '../common/ValidationErrorText'

const CampusImages = ({
  label = 'Campus Images',
  value = [],
  onChange,
  readOnly = false,
  error = '',
  setError = () => {},
  fieldError,
  fieldErrors,
  fieldType
}) => {
  const fileInputRef = useRef(null)

  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadCount, setUploadCount] = useState({ done: 0, total: 0 })

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files || [])
    e.target.value = ''

    if (!selectedFiles.length) return
    const valid = []
    for (const file of selectedFiles) {
      const isValidFormat = ['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)
      const isValidSize = file.size <= 2 * 1024 * 1024
      if (!isValidFormat) {
        setError(`Invalid file format: ${file.name}`)
        continue
      }
      if (!isValidSize) {
        setError(`File too large (max 2MB): ${file.name}`)
        continue
      }
      valid.push(file)
    }

    if (!valid.length) return

    setError('')
    setUploading(true)
    setUploadProgress(0)
    setUploadCount({ done: 0, total: valid.length })

    let completed = 0
    const bump = () => {
      completed += 1
      setUploadCount({ done: completed, total: valid.length })
      setUploadProgress(Math.round((completed / valid.length) * 100))
    }

    const tasks = valid.map((file) =>
      fileUpload(file)
        .then((data) => {
          bump()
          return data?.url || null
        })
        .catch(() => {
          bump()
          return null
        })
    )

    try {
      const results = await Promise.allSettled(tasks)
      const urls = results
        .map((r) => (r.status === 'fulfilled' ? r.value : null))
        .filter(Boolean)

      if (urls.length) onChange([...value, ...urls])

      const failed = valid.length - urls.length
      if (failed > 0) setError(`${failed} file(s) failed to upload.`)
    } finally {
      setUploading(false)
      setUploadProgress(0)
      setUploadCount({ done: 0, total: 0 })
    }
  }

  const handleRemove = async (index) => {
    setError('')
    const url = value[index]
    if (url) {
      try {
        await fileDelete(url)
      } catch {
        setError('Failed to delete image from server')
      }
    }
    onChange(value.filter((_, i) => i !== index))
  }

  return (
    <Box mt={4}>
      <Typography fontSize="14px" fontWeight="500" mb={1}>
        {label}<span style={{ color: 'red' }}>*</span>
      </Typography>
      <Typography fontSize="12px" color="textSecondary" mb={2}>
        Upload JPG/PNG images (max 2MB), 1280x720px or higher, 16:9 or 4:3 aspect ratio.
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setError('')}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/jpeg,image/jpg,image/png"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        disabled={readOnly || uploading}
      />

      <Grid container spacing={2}>
        {value.map((url, index) => (
          <Grid key={url || index} item xs={6} md={3}>
            <Box position="relative">
              <Box
                component="img"
                src={url}
                alt={`Campus ${index + 1}`}
                sx={{
                  width: '100%',
                  height: 120,
                  objectFit: 'cover',
                  borderRadius: 1,
                  border: '1px solid #ccc',
                }}
              />
              {!readOnly && (
                <IconButton
                  size="small"
                  onClick={() => handleRemove(index)}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    padding: '2px',
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Grid>
        ))}

        {!readOnly && (
          <Grid item xs={6} md={3}>
            <Box
              onClick={() => !uploading && fileInputRef.current?.click()}
              sx={{
                width: '100%',
                height: 120,
                border: '1px dashed #0463EF',
                background: 'rgba(4, 99, 239, 0.06)',
                borderRadius: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: uploading ? 'not-allowed' : 'pointer',
                position: 'relative',
                textAlign: 'center',
                p: 1.5,
              }}
            >
              {uploading ? (
                <Box display="flex" flexDirection="column" alignItems="center">
                  <CircularProgress size={24} thickness={5} />
                  <Typography fontSize={11} mt={1}>
                    Uploading… {uploadCount.done}/{uploadCount.total} ({uploadProgress}%)
                  </Typography>
                </Box>
              ) : (
                <Typography
                  fontSize="13px"
                  fontWeight="500"
                  color="#363853"
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}
                >
                  <AddIcon sx={{ fill: '#1075FF' }} />
                  Click to upload
                </Typography>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
      <ValidationErrorText error={fieldError}/>
    </Box>
  )
}

export default CampusImages
